import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, LogOut } from "lucide-react";
import { sessionManager } from "../utils/sessionManager";
import { LanguageContext } from "../constant/LanguageContext";
import { ThemeContext } from "../constant/theme";

// Shared across tabs (localStorage, not sessionStorage) — activity in ANY
// open tab resets the idle clock for ALL of them, and a 'storage' event
// fires in every OTHER tab the instant one tab records activity, so the
// warning dismisses everywhere in sync rather than each tab running its own
// unsynchronized 15-minute clock.
const LAST_ACTIVITY_KEY = "app_last_activity";
// Only write to localStorage at most this often — mousemove fires dozens of
// times a second, and every write is a 'storage' event broadcast to every
// other tab.
const ACTIVITY_WRITE_THROTTLE_MS = 2000;
const TICK_MS = 1000;
// Sits above literally everything else in the app (the highest existing
// z-index anywhere in the codebase was 999999, shared by a couple of other
// full-screen modals — if one of those happened to mount after this one,
// equal z-index falls back to DOM order and could paint over the warning).
const MAX_Z_INDEX = 2147483647;

const ACTIVITY_EVENTS = ["mousemove", "keydown", "mousedown", "touchstart", "scroll", "wheel"];

/**
 * Replaces the old silent "IdleRedirect" — instead of logging the user out
 * the instant 15 minutes of inactivity elapses, this shows an interruptive,
 * full-screen warning with a countdown for the last `warningTime` ms of that
 * window.
 *
 * Two things this deliberately does NOT do, per feedback on the first pass:
 *  1. It does not dismiss on passive mouse movement/scroll once it's up —
 *     only an explicit "Stay Signed In" click (or Enter, since focus is
 *     forced onto that button) counts as confirming someone is actually
 *     there. Before the warning appears, though, normal activity still
 *     resets the idle clock exactly as before — this restriction only
 *     kicks in once it's already showing.
 *  2. It blocks the page underneath while showing — background scroll is
 *     disabled, focus is trapped inside the dialog, and it sits at the
 *     highest possible stacking order so nothing else in the app (chat
 *     dock, other modals, dropdowns) can render on top of it.
 */
export default function IdleSessionGuard({ to = "/login", idleTime = 900000, warningTime = 60000 }) {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext) || {};
  const { theme } = useContext(ThemeContext) || {};
  const isRTL = language === "ar";
  const isDark = theme === "dark";

  const [showWarning, setShowWarningState] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(warningTime / 1000));

  const lastActivityRef = useRef(Date.now());
  const lastWriteRef = useRef(0);
  // Mirrors `showWarning` synchronously so the (mount-once) event listeners
  // and the ticking interval always read the CURRENT value instead of
  // whatever was captured in a stale closure — avoids having to tear down
  // and re-subscribe every listener each time the warning opens/closes.
  const showWarningRef = useRef(false);
  const stayButtonRef = useRef(null);

  const setShowWarning = (value) => {
    showWarningRef.current = value;
    setShowWarningState(value);
  };

  useEffect(() => {
    // Passive activity (mouse/keyboard/scroll anywhere on the page) only
    // resets the idle clock BEFORE the warning is showing. Once it's up,
    // this is intentionally ignored — see the "does NOT dismiss on
    // movement" note above.
    const handlePassiveActivity = () => {
      if (showWarningRef.current) return;
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastWriteRef.current > ACTIVITY_WRITE_THROTTLE_MS) {
        lastWriteRef.current = now;
        try {
          localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
        } catch (e) {
          // storage unavailable — this tab still tracks activity locally
        }
      }
    };
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handlePassiveActivity, { passive: true }));

    // Another tab recorded real activity (or had its warning explicitly
    // confirmed) — pick that up immediately rather than waiting for this
    // tab's own next 1s tick, so it dismisses everywhere in sync.
    const onStorage = (e) => {
      if (e.key !== LAST_ACTIVITY_KEY || !e.newValue) return;
      const shared = parseInt(e.newValue, 10);
      if (Number.isFinite(shared) && shared > lastActivityRef.current) {
        lastActivityRef.current = shared;
        if (showWarningRef.current) setShowWarning(false);
      }
    };
    window.addEventListener("storage", onStorage);

    const interval = setInterval(() => {
      // Don't run at all on the login page / while logged out — nothing to
      // warn about.
      if (!sessionManager.isAuthenticated()) {
        if (showWarningRef.current) setShowWarning(false);
        return;
      }

      const stored = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY), 10);
      const lastActivity = Number.isFinite(stored) ? Math.max(stored, lastActivityRef.current) : lastActivityRef.current;
      const idleMs = Date.now() - lastActivity;

      if (idleMs >= idleTime) {
        // Truly out of time — log out for real, in every tab (sessionManager
        // .logout() broadcasts LOGOUT_BROADCAST_KEY, same as a manual logout).
        setShowWarning(false);
        sessionManager.logout();
        navigate(to);
        return;
      }

      const warnAt = idleTime - warningTime;
      if (idleMs >= warnAt) {
        setSecondsLeft(Math.max(0, Math.ceil((idleTime - idleMs) / 1000)));
        if (!showWarningRef.current) setShowWarning(true);
      } else if (showWarningRef.current) {
        setShowWarning(false);
      }
    }, TICK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handlePassiveActivity));
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
    };
    // Mount-once: everything inside reads current values via refs, so this
    // never needs to tear down/rebuild on idleTime/warningTime changes in
    // practice (those props are static in App.js anyway).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Blocks background scroll and forces focus onto the dialog while it's
  // showing — the two things that make this a real interrupt rather than
  // just an overlay someone can scroll/tab past.
  useEffect(() => {
    if (!showWarning) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    stayButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showWarning]);

  // Explicit confirmation only — this is the ONE thing (besides "Log Out
  // Now") that's allowed to dismiss the warning once it's up.
  const confirmPresence = () => {
    const now = Date.now();
    lastActivityRef.current = now;
    lastWriteRef.current = now;
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    } catch (e) {
      // storage unavailable — this tab still resets locally
    }
    setShowWarning(false);
  };

  const doLogout = () => {
    setShowWarning(false);
    sessionManager.logout();
    navigate(to);
  };

  // Minimal focus trap: Tab/Shift+Tab cycles between the two buttons only,
  // Enter confirms presence — keyboard focus can't leak out to the page
  // underneath while this is up.
  const handleDialogKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      confirmPresence();
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      const focusables = e.currentTarget.querySelectorAll("button");
      if (!focusables.length) return;
      const list = Array.from(focusables);
      const currentIndex = list.indexOf(document.activeElement);
      const nextIndex = e.shiftKey
        ? (currentIndex - 1 + list.length) % list.length
        : (currentIndex + 1) % list.length;
      list[nextIndex]?.focus();
    }
    // Deliberately no Escape handling — Escape closing it would be another
    // "passive" way to bypass the confirmation without proving presence.
  };

  if (!showWarning) return null;

  const t = isRTL
    ? {
        title: "هل ما زلت موجودًا؟",
        body: `تم تسجيل خروجك تلقائيًا خلال ${secondsLeft} ثانية بسبب عدم النشاط.`,
        stay: "متابعة تسجيل الدخول",
        logout: "تسجيل الخروج الآن",
      }
    : {
        title: "Are you still there?",
        body: `You'll be signed out automatically in ${secondsLeft} second${secondsLeft === 1 ? "" : "s"} due to inactivity.`,
        stay: "Stay Signed In",
        logout: "Log Out Now",
      };

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-live="assertive"
      onKeyDown={handleDialogKeyDown}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: MAX_Z_INDEX,
        background: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 18,
          padding: 28,
          background: isDark ? "#1e293b" : "#ffffff",
          color: isDark ? "#f1f5f9" : "#0f172a",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          textAlign: isRTL ? "right" : "left",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexDirection: isRTL ? "row-reverse" : "row" }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: isDark ? "rgba(251,191,36,0.15)" : "#fef3c7",
              color: "#d97706",
            }}
          >
            <AlertTriangle size={24} />
          </div>
          <h2 style={{ fontSize: 19, fontWeight: 800, margin: 0 }}>{t.title}</h2>
        </div>

        <p style={{ fontSize: 14.5, lineHeight: 1.6, margin: "0 0 22px", color: isDark ? "#cbd5e1" : "#475569" }}>
          {t.body}
        </p>

        <div
          style={{
            fontSize: 34, fontWeight: 800, textAlign: "center", marginBottom: 22,
            color: secondsLeft <= 10 ? "#dc2626" : (isDark ? "#f1f5f9" : "#0f172a"),
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {secondsLeft}s
        </div>

        <div style={{ display: "flex", gap: 10, flexDirection: isRTL ? "row-reverse" : "row" }}>
          <button
            type="button"
            onClick={doLogout}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 10, cursor: "pointer",
              fontSize: 13.5, fontWeight: 700,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)"}`,
              background: "transparent", color: isDark ? "#e2e8f0" : "#334155",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <LogOut size={15} />
            {t.logout}
          </button>
          <button
            type="button"
            ref={stayButtonRef}
            onClick={confirmPresence}
            style={{
              flex: 1, padding: "11px 16px", borderRadius: 10, border: "none", cursor: "pointer",
              fontSize: 13.5, fontWeight: 700, color: "#fff",
              background: "linear-gradient(135deg, #3b82f6, #2563eb)",
            }}
          >
            {t.stay}
          </button>
        </div>
      </div>
    </div>
  );
}
